import { Image, MEDIA_BELOW_MD, MEDIA_MD_UP } from "hec/components/image";
import { TextBlock } from "hec/components/text-block";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  ContentButtonNavigation,
} from "hec/components/ui/carousel";
import { useId } from "react";

import { builderImageUrl } from "~/hec/utils/builder-image-url";

import type { IThreeColumnProps } from "./types";
import { getResponsiveHeroBgStyles } from "./utils";

const ThreeColumn = (props: IThreeColumnProps) => {
  const baseId = useId();
  const {
    backgroundType,
    backgroundImage,
    backgroundImageMobile,
    backgroundColor,
    backgroundGradient,
    leftImage,
    rightImage,
    mobileImageLeft,
    mobileImageRight,
    external,
    external_link,
    link,
    couponCode,
    enableCouponCode,
    eyebrowText,
    eyebrowIcon,
    eyebrowType,
    disclaimerText,
    headlineType,
    headlineImage,
    headlineText,
    textBlock,
    callToAction,
    buttonColor,
    enableOverlay,
    overlayColor,
    textAlignment,
    eyebrowBackgroundColor,
    eyebrowSize,
    eyebrowTextColor,
    carouselItems = [],
    carouselDots,
    priority = false,
  } = props;

  const resolvedLink = external
    ? (external_link?.value?.data?.url ?? "")
    : (link?.value?.data?.url ?? "");

  const heroId = `hero-3c-${baseId.replace(/:/g, "")}`;
  const { style: bgStyle, mobileCss } = getResponsiveHeroBgStyles({
    id: heroId,
    backgroundType,
    backgroundImage,
    backgroundImageMobile,
    backgroundColor,
    backgroundGradient,
  });

  return backgroundType === "carousel" ? (
    <Carousel
      opts={{
        align: "start",
        loop: true,
      }}
      className="w-full"
    >
      <CarouselContent>
        {carouselItems.map((item, index) => {
          const carouselHeroId = `hero-3c-c-${baseId.replace(/:/g, "")}-${index}`;
          const { style: carouselBgStyle, mobileCss: carouselMobileCss } =
            getResponsiveHeroBgStyles({
              id: carouselHeroId,
              backgroundType: item.carouselBackgroundType || "image",
              backgroundImage: item.backgroundImage,
              backgroundImageMobile: item.backgroundImageMobile,
              backgroundColor: item.backgroundColor,
              backgroundGradient: item.backgroundGradient,
            });

          const carouselResolvedLink = item.external
            ? (item.external_link?.value?.data?.url ?? "")
            : (item.link?.value?.data?.url ?? "");

          // For the three-column mobile carousel, we need a separate mobile bg
          // when the carousel item has a specific mobile background image
          const carouselMobileBgCss =
            item.carouselBackgroundType === "image" &&
            item.backgroundImageMobile
              ? `@media (max-width: 767px) { #${carouselHeroId}-mobile { background-image: url(${builderImageUrl(item.backgroundImageMobile, { width: 800, format: "webp" })}); background-size: cover; background-position: center; } }`
              : "";

          return (
            <CarouselItem key={index}>
              {carouselMobileCss && <style>{carouselMobileCss}</style>}
              {carouselMobileBgCss && <style>{carouselMobileBgCss}</style>}
              <div
                id={carouselHeroId}
                className="relative flex flex-col pb-8 pt-8"
                style={carouselBgStyle}
              >
                {/* Desktop layout */}
                <div className="ml-auto mr-auto hidden w-full flex-row gap-8 md:flex">
                  <div className="ml-auto mr-auto grid w-full grid-cols-12 items-center gap-8">
                    <div className="col-span-3 flex justify-center">
                      <Image
                        src={item.leftImage}
                        alt="Left Image"
                        priority={priority && index === 0}
                        media={MEDIA_MD_UP}
                      />
                    </div>
                    <div className="col-span-6 flex flex-col items-center justify-center gap-2 text-center lg:gap-4">
                      <div className="flex max-w-[720px] flex-col items-center justify-center text-center">
                        <TextBlock
                          couponCode={item.couponCode}
                          enableCouponCode={item.enableCouponCode}
                          eyebrow={item.eyebrowText}
                          eyebrowIcon={item.eyebrowIcon}
                          eyebrowType={item.eyebrowType}
                          disclaimer={item.disclaimerText ?? ""}
                          headlineType={item.headlineType}
                          headlineImage={item.headlineImage}
                          headline={item.headlineText ?? ""}
                          supportText={item.textBlock ?? ""}
                          cta={item.callToAction ?? ""}
                          buttonColor={item.buttonColor}
                          external={item.external}
                          link={carouselResolvedLink}
                          overlay={item.enableOverlay}
                          overlayColor={item.overlayColor}
                          alignment={item.textAlignment}
                          eyebrowBackgroundColor={item.eyebrowBackgroundColor}
                          eyebrowSize={item.eyebrowSize}
                          eyebrowTextColor={item.eyebrowTextColor}
                        />
                      </div>
                    </div>
                    <div className="col-span-3 flex justify-center">
                      <Image
                        src={item.rightImage}
                        alt="Right Image"
                        priority={priority && index === 0}
                        media={MEDIA_MD_UP}
                      />
                    </div>
                  </div>
                </div>

                {/* Mobile layout */}
                <div
                  id={`${carouselHeroId}-mobile`}
                  className="flex flex-col items-center gap-2 px-4 text-center md:hidden"
                >
                  <TextBlock
                    couponCode={item.couponCode}
                    enableCouponCode={item.enableCouponCode}
                    eyebrow={item.eyebrowText}
                    eyebrowIcon={item.eyebrowIcon}
                    eyebrowType={item.eyebrowType}
                    disclaimer={item.disclaimerText ?? ""}
                    headlineType={item.headlineType}
                    headlineImage={item.headlineImage}
                    headline={item.headlineText ?? ""}
                    supportText={item.textBlock ?? ""}
                    cta={item.callToAction ?? ""}
                    buttonColor={item.buttonColor}
                    external={item.external}
                    link={carouselResolvedLink}
                    overlay={item.enableOverlay}
                    overlayColor={item.overlayColor}
                    alignment={item.textAlignment}
                    eyebrowBackgroundColor={item.eyebrowBackgroundColor}
                    eyebrowSize={item.eyebrowSize}
                    eyebrowTextColor={item.eyebrowTextColor}
                  />
                  <div className="flex w-full flex-row justify-center gap-4">
                    <div className="relative h-40 w-40 min-w-[20px] overflow-hidden align-baseline">
                      <Image
                        src={item.mobileImageLeft || item.leftImage || ""}
                        alt="Left Image"
                        priority={priority && index === 0}
                        media={MEDIA_BELOW_MD}
                        maxCssWidth={160}
                        sizes="160px"
                      />
                    </div>
                    <div className="relative h-40 w-40 min-w-[20px] overflow-hidden align-baseline">
                      <Image
                        src={item.mobileImageRight || item.rightImage || ""}
                        alt="Right Image"
                        priority={priority && index === 0}
                        media={MEDIA_BELOW_MD}
                        maxCssWidth={160}
                        sizes="160px"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </CarouselItem>
          );
        })}
      </CarouselContent>
      {carouselItems.length > 1 && carouselDots !== false && (
        <ContentButtonNavigation
          buttonType="circleButton"
          data-testid="threecolumn-hero-carousel"
        />
      )}
    </Carousel>
  ) : (
    <>
      {mobileCss && <style>{mobileCss}</style>}
      {backgroundType === "image" && backgroundImageMobile && (
        <style>{`@media (max-width: 767px) { #${heroId}-mobile { background-image: url(${builderImageUrl(backgroundImageMobile, { width: 800, format: "webp" })}); background-size: cover; background-position: center; } }`}</style>
      )}
      <div
        id={heroId}
        className="relative flex flex-col pb-8 pt-8"
        style={bgStyle}
      >
        {/* Desktop layout */}
        <div className="ml-auto mr-auto hidden w-full flex-row gap-8 md:flex">
          <div className="ml-auto mr-auto grid w-full grid-cols-12 items-center gap-8">
            <div className="col-span-3 flex justify-center">
              <Image
                src={leftImage}
                alt="Left Image"
                priority={priority}
                media={MEDIA_MD_UP}
                maxCssWidth={320}
                sizes="(min-width: 768px) 25vw, 160px"
              />
            </div>
            <div className="col-span-6 flex flex-col items-center justify-center gap-2 text-center lg:gap-4">
              <div className="flex max-w-[720px] flex-col items-center justify-center text-center">
                <TextBlock
                  couponCode={couponCode}
                  enableCouponCode={enableCouponCode}
                  eyebrow={eyebrowText}
                  eyebrowIcon={eyebrowIcon}
                  eyebrowType={eyebrowType}
                  disclaimer={disclaimerText ?? ""}
                  headlineType={headlineType}
                  headlineImage={headlineImage}
                  headline={headlineText ?? ""}
                  supportText={textBlock ?? ""}
                  cta={callToAction ?? ""}
                  buttonColor={buttonColor}
                  external={external}
                  link={resolvedLink}
                  overlay={enableOverlay}
                  overlayColor={overlayColor}
                  alignment={textAlignment}
                  eyebrowBackgroundColor={eyebrowBackgroundColor}
                  eyebrowSize={eyebrowSize}
                  eyebrowTextColor={eyebrowTextColor}
                />
              </div>
            </div>
            <div className="col-span-3 flex justify-center">
              <Image
                src={rightImage}
                alt="Right Image"
                priority={priority}
                media={MEDIA_MD_UP}
                maxCssWidth={320}
                sizes="(min-width: 768px) 25vw, 160px"
              />
            </div>
          </div>
        </div>

        {/* Mobile layout */}
        <div
          id={`${heroId}-mobile`}
          className="flex flex-col items-center gap-2 px-4 text-center md:hidden"
        >
          <TextBlock
            couponCode={couponCode}
            enableCouponCode={enableCouponCode}
            eyebrow={eyebrowText}
            eyebrowIcon={eyebrowIcon}
            eyebrowType={eyebrowType}
            disclaimer={disclaimerText ?? ""}
            headlineType={headlineType}
            headlineImage={headlineImage}
            headline={headlineText ?? ""}
            supportText={textBlock ?? ""}
            cta={callToAction ?? ""}
            buttonColor={buttonColor}
            external={external}
            link={resolvedLink}
            overlay={enableOverlay}
            overlayColor={overlayColor}
            alignment={textAlignment}
            eyebrowBackgroundColor={eyebrowBackgroundColor}
            eyebrowSize={eyebrowSize}
            eyebrowTextColor={eyebrowTextColor}
          />
          <div className="flex w-full flex-row justify-center gap-4">
            <div className="relative h-40 w-40 min-w-[20px] overflow-hidden align-baseline">
              <Image
                src={mobileImageLeft || leftImage || ""}
                alt="Left Image"
                priority={priority}
                media={MEDIA_BELOW_MD}
                maxCssWidth={160}
                sizes="160px"
              />
            </div>
            <div className="relative h-40 w-40 min-w-[20px] overflow-hidden align-baseline">
              <Image
                src={mobileImageRight || rightImage || ""}
                alt="Right Image"
                priority={priority}
                media={MEDIA_BELOW_MD}
                maxCssWidth={160}
                sizes="160px"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ThreeColumn;
